import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';

// Aegis Management Task status constants from backend model
export const AegisManagementStatus = {
  PENDING: 'AMT_PENDING',
  RUNNING: 'AMT_RUNNING',
  COMPLETED: 'AMT_COMPLETED',
  FAILED: 'AMT_FAILED',
} as const;

export interface AegisManagementCapability {
  runs_on: string;
  category: string;
  executor_type: string;
  long_running: boolean;
  inputs: {
    name: string;
    title: string;
    artifact: string;
    target: string;
    description: string;
    author: string;
    reference?: string[];
    tools?: Array<{
      name: string;
      path?: string;
      url?: string;
      primary: boolean;
    }>;
    env?: Array<{
      name: string;
      description: string;
      type: string;
      default?: string;
      required?: boolean;
      sensitive?: boolean;
      choices?: string[];
    }>;
    command?: string;
  };
  output: {
    parser: string;
  };
}

// Request type for creating a new task (only required fields)
export interface CreateAegisManagementTaskRequest {
  aegisManagementCapability: string;
  aegisClientId: string;
  parameters?: Record<string, string>;
  aegisAgentId?: string;
}

/**
 * Interface for the result of a command execution
 */
export interface AegisCommandResult {
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  duration?: number;
  pid?: number;
}

/**
 * Discriminated union for API error types to improve type safety
 */
export type ApiError =
  | { type: 'network'; message: string; name?: string }
  | {
      type: 'validation';
      response: { data: { message: string; details?: Record<string, string> } };
      name?: string;
    }
  | {
      type: 'server';
      response: { data: { message: string; code?: string } };
      name?: string;
    }
  | {
      type: 'unknown';
      message?: string;
      response?: { data?: { message?: string } };
      name?: string;
    };

/**
 * Complete task type returned from the server
 */
export interface AegisManagementTask {
  username: string;
  key: string;
  aegisManagementCapability: string;
  aegisClientId: string;
  aegisAgentId?: string;
  flowId?: string;
  parameters: Record<string, string>;
  status: string;
  created: string;
  updated: string;
  started?: string;
  completed?: string;
  result?: string;
  errorMessage?: string;
  commandResult?: AegisCommandResult;
  async: boolean;
  ttl: number;
}

export interface TunnelRequest {
  aegisClientId: string;
}

export interface TunnelResponse {
  tunnelInfo?: {
    accountTag: string;
    tunnelId: string;
    tunnelName: string;
  };
  hostname?: string;
  installTaskId?: string;
  message: string;
  aegisClientId?: string;
  os?: string;
}

export interface TaskResponse {
  taskId: string;
  status: string;
  message: string;
}

/**
 * Custom hook for managing Aegis security agent operations
 *
 * Provides comprehensive management capabilities for Aegis agents including:
 * - Cloudflare tunnel provisioning and removal
 * - Linux user management (add/remove users)
 * - Task creation, monitoring, and cancellation
 * - Real-time task status updates
 *
 * @param agentId - Optional agent client ID to filter tasks for specific agent
 * @param options - Optional configuration object
 * @param options.enabled - Whether polling should be enabled (useful for modal state)
 * @returns Object containing capabilities, tasks, loading states, and management functions
 *
 * @example
 * ```typescript
 * const { tasks, createTunnel, createTask, isLoading } = useAegisManagement(agent?.client_id, { enabled: modalOpen });
 *
 * // Create a tunnel
 * await createTunnel(agent.client_id);
 *
 * // Add a user
 * await createTask({
 *   aegisManagementCapability: 'linux-system-adduser',
 *   aegisClientId: agent.client_id,
 *   parameters: { username: 'newuser' }
 * });
 * ```
 */
export const useAegisManagement = (
  agentId?: string,
  options?: { enabled?: boolean }
) => {
  const axios = useAxios();

  // Get capabilities with proper typing
  const { data: capabilities, isLoading: capabilitiesLoading } = useQuery<
    Record<string, AegisManagementCapability>
  >({
    queryKey: ['aegis-management-capabilities'],
    queryFn: async (): Promise<Record<string, AegisManagementCapability>> => {
      const response = await axios.get<
        Record<string, AegisManagementCapability>
      >('/aegis/management/capabilities');
      return response.data;
    },
    select: (data): Record<string, AegisManagementCapability> => data ?? {},
    enabled: true,
  });

  // Get tasks for the current agent with smart polling and proper typing
  const { data: tasks, isLoading: tasksLoading } = useQuery<
    AegisManagementTask[]
  >({
    queryKey: ['aegis-management-tasks', agentId],
    queryFn: async (): Promise<AegisManagementTask[]> => {
      if (!agentId) return [];
      // Try to get all tasks and filter client-side if backend doesn't support direct filtering
      try {
        const response = await axios.get<AegisManagementTask[]>(
          '/aegis/management/tasks'
        );
        const allTasks = response.data || [];
        // Filter tasks for this specific agent (using the correct field name from the model)
        return allTasks.filter(
          (task: AegisManagementTask) => task.aegisClientId === agentId
        );
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
        return [];
      }
    },
    enabled: !!agentId && options?.enabled !== false,
    refetchInterval: query => {
      // Smart polling: only poll when enabled and if there are active tasks
      if (!options?.enabled) return false;
      const data = query.state.data;
      if (!Array.isArray(data)) return false;
      const hasActiveTasks = data.some(
        (task: AegisManagementTask) =>
          task.status === AegisManagementStatus.PENDING ||
          task.status === AegisManagementStatus.RUNNING
      );
      return hasActiveTasks ? 3000 : false; // Poll every 3 seconds if active tasks exist
    },
    staleTime: 2000, // Consider data stale after 2 seconds
    select: (data): AegisManagementTask[] => data ?? [],
  });

  // Create tunnel mutation
  const createTunnelMutation = useMutation({
    mutationFn: async (aegisClientId: string): Promise<TunnelResponse> => {
      const response = await axios.post(
        '/aegis/management/cloudflare/tunnel/create',
        {
          aegisClientId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aegis-management-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (error: ApiError) => {
      let message: string;
      switch (error.type) {
        case 'network':
          message =
            error.message || 'Network error occurred while creating tunnel';
          break;
        case 'validation':
          message =
            error.response.data.message ||
            'Invalid data provided for tunnel creation';
          break;
        case 'server':
          message =
            error.response.data.message ||
            'Server error occurred while creating tunnel';
          break;
        case 'unknown':
        default:
          message =
            error.response?.data?.message ||
            error.message ||
            'Failed to create tunnel';
          break;
      }
      toast.error(message);
    },
  });

  // Remove tunnel mutation
  const removeTunnelMutation = useMutation({
    mutationFn: async (aegisClientId: string) => {
      const response = await axios.post(
        '/aegis/management/cloudflare/tunnel/remove',
        {
          aegisClientId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aegis-management-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (error: ApiError) => {
      let message: string;
      switch (error.type) {
        case 'network':
          message =
            error.message || 'Network error occurred while removing tunnel';
          break;
        case 'validation':
          message =
            error.response.data.message ||
            'Invalid data provided for tunnel removal';
          break;
        case 'server':
          message =
            error.response.data.message ||
            'Server error occurred while removing tunnel';
          break;
        case 'unknown':
        default:
          message =
            error.response?.data?.message ||
            error.message ||
            'Failed to remove tunnel';
          break;
      }
      toast.error(message);
    },
  });

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (
      task: CreateAegisManagementTaskRequest
    ): Promise<TaskResponse> => {
      const response = await axios.post('/aegis/management/tasks', task);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aegis-management-tasks'] });
    },
    onError: (error: ApiError) => {
      let message: string;
      switch (error.type) {
        case 'network':
          message =
            error.message || 'Network error occurred while creating task';
          break;
        case 'validation':
          message = error.response.data.message || 'Invalid task data provided';
          break;
        case 'server':
          message =
            error.response.data.message ||
            'Server error occurred while creating task';
          break;
        case 'unknown':
        default:
          message =
            error.response?.data?.message ||
            error.message ||
            'Failed to create task';
          break;
      }
      toast.error(message);
    },
  });

  // Cancel task mutation
  const cancelTaskMutation = useMutation({
    mutationFn: async (task: { key: string }) => {
      const response = await axios.delete('/aegis/management/tasks', {
        data: task,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aegis-management-tasks'] });
      toast.success('Task cancellation requested');
    },
    onError: (error: ApiError) => {
      let message: string;
      switch (error.type) {
        case 'network':
          message =
            error.message || 'Network error occurred while canceling task';
          break;
        case 'validation':
          message =
            error.response.data.message ||
            'Invalid task key provided for cancellation';
          break;
        case 'server':
          message =
            error.response.data.message ||
            'Server error occurred while canceling task';
          break;
        case 'unknown':
        default:
          message =
            error.response?.data?.message ||
            error.message ||
            'Failed to cancel task';
          break;
      }
      toast.error(message);
    },
  });

  // Get specific task
  const getTask = async (taskKey: string) => {
    try {
      const response = await axios.get('/aegis/management/tasks', {
        params: { key: taskKey },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get task:', error);
      throw error;
    }
  };

  return {
    capabilities,
    tasks,
    capabilitiesLoading,
    tasksLoading,
    createTunnel: createTunnelMutation.mutateAsync,
    removeTunnel: removeTunnelMutation.mutateAsync,
    createTask: createTaskMutation.mutateAsync,
    cancelTask: cancelTaskMutation.mutateAsync,
    getTask,
    isLoading:
      capabilitiesLoading ||
      tasksLoading ||
      createTunnelMutation.isPending ||
      removeTunnelMutation.isPending ||
      createTaskMutation.isPending,
  };
};

// Hook to check if an agent has cloudflare configured
export const useAgentCloudflareStatus = (agent?: Agent) => {
  const cloudflaredStatus = agent?.health_check?.cloudflared_status;

  // Match the same logic from agents/index.tsx
  const isRunning = cloudflaredStatus?.status === 'running';
  const isConnected = cloudflaredStatus?.connected === 'true';
  const hasCloudflare = isRunning && isConnected;

  const hostname = cloudflaredStatus?.hostname;
  const tunnelName = cloudflaredStatus?.tunnel_name;

  return {
    hasCloudflare,
    isRunning,
    isConnected,
    hostname,
    tunnelName,
  };
};
